import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import FormRow from '../../Common/Components/FormRow'
import Fieldset from '../../Common/Components/Fieldset'
import SelectMulti from '../../Common/Components/Selects/SelectMulti'
import DateTimePicker, { DateTimePickerType, DateTimePickerVariant } from '../../Common/Components/DateTimePicker'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import {
    SCHEDULEREPORT_DAILY_OPTION_VALUE,
    SCHEDULEREPORT_HOURLY_OPTION_VALUE,
    SCHEDULEREPORT_MONTHLY_OPTION_VALUE,
    SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE,
    SCHEDULEREPORT_ONCE_OPTION_VALUE,
    SCHEDULEREPORT_WEEKLY_OPTION_VALUE
} from './Constants'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import {
    DaysOfTheWeek as DaysOfTheWeekLanguage,
    TimeOptions as TimeOptionsLanguage,
    Date as DateLanguage
} from '../../../../i18n'

import { HeadingLevel } from '../../Common/Components/Heading/Heading'

/**
 * Component properties type.
 */
type Props = {
    scheduleReportOn: number
    runOnDate: Date | null
    setRunOnDate: (newValue: Date) => void
    runAtTime: Date
    setRunAtTime: (newValue: Date) => void
    runOnDays: string[]
    setRunOnDays: (newValue: string[]) => void
    runAtMinute: Date
    setRunAtMinute: (newValue: Date) => void
    runOnDayInWeek: string
    setRunOnDayInWeek: (newValue: string) => void
    runOnDayInMonth: number
    setRunOnDayInMonth: (newValue: number) => void
    runBetweenStart: Date
    setRunBetweenStart: (newValue: Date) => void
    runBetweenEnd: Date
    setRunBetweenEnd: (newValue: Date) => void
}

/**
 * Time Options section in scheduling tab of report configuration modal window.
 *
 * @param scheduleReportOn Report property.
 * @param runOnDate Report property.
 * @param setRunOnDate Setter for corresponding report property.
 * @param runAtTime Report property.
 * @param setRunAtTime Setter for corresponding report property.
 * @param runOnDays Report property.
 * @param setRunOnDays Setter for corresponding report property.
 * @param runAtMinute Report property.
 * @param setRunAtMinute Setter for corresponding report property.
 * @param runOnDayInWeek Report property.
 * @param setRunOnDayInWeek Setter for corresponding report property.
 * @param runOnDayInMonth Report property.
 * @param setRunOnDayInMonth Setter for corresponding report property.
 * @param runBetweenStart Report property.
 * @param setRunBetweenStart Setter for corresponding report property.
 * @param runBetweenEnd Report property.
 * @param setRunBetweenEnd Setter for corresponding report property.
 */
const TimeOptions: React.FC<Props> = ({
    scheduleReportOn,
    runOnDate,
    setRunOnDate,
    runAtTime,
    setRunAtTime,
    runOnDays,
    setRunOnDays,
    runAtMinute,
    setRunAtMinute,
    runOnDayInWeek,
    setRunOnDayInWeek,
    runOnDayInMonth,
    setRunOnDayInMonth,
    runBetweenStart,
    setRunBetweenStart,
    runBetweenEnd,
    setRunBetweenEnd
}): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('TimeOptions')

    const displaySchedulingOptions = SCHEDULEREPORT_NOTSCHEDULED_OPTION_VALUE !== scheduleReportOn

    const displayRunAtTime =
        displaySchedulingOptions &&
        [
            SCHEDULEREPORT_MONTHLY_OPTION_VALUE,
            SCHEDULEREPORT_WEEKLY_OPTION_VALUE,
            SCHEDULEREPORT_DAILY_OPTION_VALUE,
            SCHEDULEREPORT_ONCE_OPTION_VALUE
        ].indexOf(scheduleReportOn) !== -1

    const displayRunOnDays =
        displaySchedulingOptions &&
        [SCHEDULEREPORT_HOURLY_OPTION_VALUE, SCHEDULEREPORT_DAILY_OPTION_VALUE].indexOf(scheduleReportOn) !== -1

    const displayRunOnDayInWeek = displaySchedulingOptions && SCHEDULEREPORT_WEEKLY_OPTION_VALUE === scheduleReportOn

    const displayRunOnDayInMonth = displaySchedulingOptions && SCHEDULEREPORT_MONTHLY_OPTION_VALUE === scheduleReportOn

    const displayRunOnDate = displaySchedulingOptions && SCHEDULEREPORT_ONCE_OPTION_VALUE === scheduleReportOn

    const displayRunBetweenHours = displaySchedulingOptions && SCHEDULEREPORT_HOURLY_OPTION_VALUE === scheduleReportOn

    const displayRunAtMinute = displayRunBetweenHours

    const daysOfTheWeek: SelectOptions<string> = [
        {
            label: `${language?.get(DaysOfTheWeekLanguage.Monday)}`,
            value: `${language?.get(DaysOfTheWeekLanguage.Mondayshort)}`
        },
        {
            label: `${language?.get(DaysOfTheWeekLanguage.Tuesday)}`,
            value: `${language?.get(DaysOfTheWeekLanguage.Tuesdayshort)}`
        },
        {
            label: `${language?.get(DaysOfTheWeekLanguage.Wednesday)}`,
            value: `${language?.get(DaysOfTheWeekLanguage.Wednesdayshort)}`
        },
        {
            label: `${language?.get(DaysOfTheWeekLanguage.Thursday)}`,
            value: `${language?.get(DaysOfTheWeekLanguage.Thursdayshort)}`
        },
        {
            label: `${language?.get(DaysOfTheWeekLanguage.Friday)}`,
            value: `${language?.get(DaysOfTheWeekLanguage.Fridayshort)}`
        },
        {
            label: `${language?.get(DaysOfTheWeekLanguage.Saturday)}`,
            value: `${language?.get(DaysOfTheWeekLanguage.Saturdayshort)}`
        },
        {
            label: `${language?.get(DaysOfTheWeekLanguage.Sunday)}`,
            value: `${language?.get(DaysOfTheWeekLanguage.Sundayshort)}`
        }
    ]

    const daysOfTheMonth: SelectOptions<number> = [...Array(31).keys()].map((dayIndex) => ({
        label: String(dayIndex + 1),
        value: dayIndex + 1
    }))

    return (
        <Fieldset className='scheduling-time-options'>
            <Heading level={HeadingLevel.ONE}>{language.get(TimeOptionsLanguage.TimeOptions_SectionHeader)}</Heading>
            {displayRunOnDate && (
                <FormRow isInline className={'run-on-date'}>
                    <DateTimePicker
                        type={DateTimePickerType.DATE}
                        variant={DateTimePickerVariant.INLINE}
                        value={runOnDate}
                        handleChange={setRunOnDate}
                        format={`${language.get(DateLanguage.DatePattern)}`}
                        labelLeft={language?.get(TimeOptionsLanguage.RunondateLabel)}
                    />
                </FormRow>
            )}
            {displayRunOnDays && (
                <FormRow isInline className={'run-on-days'}>
                    <SelectMulti
                        label={language?.get(TimeOptionsLanguage.RunondaysLabel)}
                        options={daysOfTheWeek}
                        value={runOnDays}
                        handleChange={setRunOnDays}
                        renderSelectedValue={true}
                    />
                </FormRow>
            )}
            {displayRunBetweenHours && (
                <FormRow isInline className={'run-between-hours'}>
                    <DateTimePicker
                        type={DateTimePickerType.TIME}
                        variant={DateTimePickerVariant.INLINE}
                        value={runBetweenStart}
                        handleChange={setRunBetweenStart}
                        format={'HH'}
                        labelLeft={language?.get(TimeOptionsLanguage.RunbetweenhoursStartLabel)}
                        timeViews={['hours']}
                        isAMPMDisplayed={false}
                    />
                    <div className='end-time-picker'>
                        <DateTimePicker
                            type={DateTimePickerType.TIME}
                            variant={DateTimePickerVariant.INLINE}
                            value={runBetweenEnd}
                            handleChange={setRunBetweenEnd}
                            format={'HH'}
                            labelLeft={language?.get(TimeOptionsLanguage.RunbetweenhoursEndLabel)}
                            timeViews={['hours']}
                            isAMPMDisplayed={false}
                        />
                    </div>
                </FormRow>
            )}
            {displayRunOnDayInWeek && (
                <FormRow isInline className={'run-on-day-of-week'}>
                    <Select
                        label={language?.get(TimeOptionsLanguage.RunondayWeekLabel)}
                        options={daysOfTheWeek}
                        value={runOnDayInWeek}
                        handleChange={setRunOnDayInWeek}
                        renderSelectedValue={false}
                    />
                </FormRow>
            )}
            {displayRunOnDayInMonth && (
                <FormRow isInline className={'run-on-day-in-month'}>
                    <Select
                        label={language?.get(TimeOptionsLanguage.RunondayMonthLabel)}
                        options={daysOfTheMonth}
                        value={runOnDayInMonth}
                        handleChange={setRunOnDayInMonth}
                    />
                </FormRow>
            )}
            {displayRunAtMinute && (
                <FormRow isInline className={'run-at-minute'}>
                    <DateTimePicker
                        type={DateTimePickerType.TIME}
                        variant={DateTimePickerVariant.INLINE}
                        value={runAtMinute}
                        handleChange={setRunAtMinute}
                        format={'mm'}
                        labelLeft={language?.get(TimeOptionsLanguage.RunatminuteHourLabel)}
                        timeViews={['minutes']}
                        timeMinutesStep={5}
                        isAMPMDisplayed={false}
                    />
                </FormRow>
            )}
            {displayRunAtTime && (
                <FormRow isInline className={'run-at-time'}>
                    <DateTimePicker
                        type={DateTimePickerType.TIME}
                        variant={DateTimePickerVariant.INLINE}
                        value={runAtTime}
                        handleChange={setRunAtTime}
                        format={'HH:mm'}
                        labelLeft={language?.get(TimeOptionsLanguage.RunattimeLabel)}
                        isAMPMDisplayed={false}
                    />
                </FormRow>
            )}
        </Fieldset>
    )
}

export default TimeOptions
